<template>
  <div class="turn-panel">
    <!-- The route's totals, so the list opens with the shape of the walk before its detail. -->
    <div class="turn-stats">
      <div class="turn-stats__item">
        <span class="turn-stats__label">Distance</span>
        <span class="turn-stats__value">{{ totalDistance }}</span>
      </div>
      <div class="turn-stats__item">
        <span class="turn-stats__label">Time</span>
        <span class="turn-stats__value">{{ duration || '—' }}</span>
      </div>
      <div class="turn-stats__item">
        <span class="turn-stats__label">Ascent</span>
        <span class="turn-stats__value">{{ totalAscent }}</span>
      </div>
    </div>

    <p v-if="rows.length === 0" class="turn-empty">
      This route has no turn instructions. They are captured when a segment is snapped, so a
      hand-drawn route has none.
    </p>

    <ol v-else class="turn-list">
      <li
        v-for="row in rows"
        :key="row.index"
        class="turn-row"
        :class="{ 'turn-row--selected': row.index === selectedIndex }"
        @click="select(row)"
      >
        <span class="material-icons turn-row__icon">{{ iconFor(row) }}</span>

        <span class="turn-row__text">
          <span class="turn-row__distance">{{ leadFor(row) }}</span>
          <span class="turn-row__instruction">{{ instructionFor(row) }}</span>
        </span>

        <!-- The running total, so a row can be found against a distance marker on the map. -->
        <span v-if="row.index > 0" class="turn-row__cumulative">in {{ cumulativeFor(row) }}</span>

        <!--
          Only a row that came from a stored entry can be changed. A seam and the closing
          arrival are synthesised by the runtime and exist nowhere in storage, so they carry
          no origin and get no menu — the right answer without a second rule to keep in step.
        -->
        <span v-if="row.origin" class="turn-row__actions">
          <button
            class="icon-btn"
            aria-haspopup="true"
            aria-label="Instruction options"
            @click.stop="openMenu(row, $event)"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>

          <ul
            v-show="menuOpen === row.index"
            class="menu-dropdown"
            :style="menuPosition"
            @mouseleave="menuOpen = null"
          >
            <li><button @click.stop="handleEdit(row)">Edit</button></li>
            <li><button class="menu-dropdown__danger" @click.stop="handleDelete(row)">Delete</button></li>
          </ul>
        </span>
      </li>
    </ol>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { DirectionRow } from '../../../utils/models/navigation/directionsList';
import { directionRowIcon } from '../../../utils/models/navigation/maneuverIcon';
import { MeasurementPrefUtility } from '../../../utils/helpers/MeasurementPref';

const props = defineProps<{
  rows: DirectionRow[];
  totalDistanceMeters: number;
  totalAscentMeters: number;
  duration: string;
}>();

const emit = defineEmits<{
  (e: 'select', row: DirectionRow): void;
  (e: 'edit', row: DirectionRow): void;
  (e: 'delete', row: DirectionRow): void;
}>();

const selectedIndex = ref<number | null>(null);
const menuOpen = ref<number | null>(null);
const menuPosition = ref<Record<string, string>>({});

// A rebuild closes any open menu — it may now sit over a different row.
//
// The selection is kept by *distance* rather than by index: an edit renumbers the rows after
// a deleted turn, so holding the index would slide the yellow bar onto its neighbour.
watch(
  () => props.rows,
  (rows, previous) => {
    menuOpen.value = null;
    const wasAt = previous?.find((row) => row.index === selectedIndex.value)?.distanceFromStartMeters;
    selectedIndex.value =
      wasAt === undefined
        ? null
        : rows.find((row) => Math.abs(row.distanceFromStartMeters - wasAt) < 1)?.index ?? null;
  }
);

/**
 * Distances in whatever units the walker reads.
 *
 * The preference lives on the account rather than here, so this only reads it — `true` lets
 * a long leg climb into the big unit instead of printing four digits of metres.
 */
function formatted(meters: number): string {
  return MeasurementPrefUtility.preference().smallUnitFormatter(meters, true);
}

// Computed, not read once at setup: the list rebuilds in place when the route changes, so a
// plain const would leave the strip showing the distance the route had when it was opened.
const totalDistance = computed(() => formatted(props.totalDistanceMeters));
const totalAscent = computed(() =>
  MeasurementPrefUtility.preference().smallUnitFormatter(props.totalAscentMeters, false)
);

const iconFor = directionRowIcon;

/**
 * The two ends of the walk are not turns, and reading them as turns is what makes a
 * directions list hard to scan — so the start is marked rather than measured. "0 m" from a
 * standing start says nothing; the distance to the finish is exactly what someone wants.
 */
function leadFor(row: DirectionRow): string {
  return row.index === 0 ? 'Start' : formatted(row.legDistanceMeters);
}

function instructionFor(row: DirectionRow): string {
  return row.isDestination ? 'End' : row.instruction;
}

function cumulativeFor(row: DirectionRow): string {
  return formatted(row.distanceFromStartMeters);
}

function select(row: DirectionRow) {
  selectedIndex.value = row.index;
  emit('select', row);
}

/**
 * Anchor the menu to the button in raw viewport coordinates.
 *
 * `PlanTrailRoutes` subtracts its panel's rect because `.route-panel` has a `backdrop-filter`,
 * which makes it a containing block for `position: fixed` children. `.action-panel` has no
 * such property, so the same offset here would push the menu off by the panel's position.
 */
function openMenu(row: DirectionRow, event: MouseEvent) {
  const button = event.currentTarget as HTMLElement | null;
  if (!button) return;

  const rect = button.getBoundingClientRect();
  const estimatedHeight = 88;
  const openUpward = rect.bottom + 8 + estimatedHeight > window.innerHeight;

  menuPosition.value = {
    top: openUpward ? `${rect.top - 8}px` : `${rect.bottom + 8}px`,
    left: `${rect.right}px`,
    transform: openUpward ? 'translateX(-100%) translateY(-100%)' : 'translateX(-100%)'
  };
  menuOpen.value = menuOpen.value === row.index ? null : row.index;
}

function handleEdit(row: DirectionRow) {
  menuOpen.value = null;
  emit('edit', row);
}

function handleDelete(row: DirectionRow) {
  menuOpen.value = null;
  emit('delete', row);
}

function closeMenuOnOutsideClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null;
  if (!target?.closest('.turn-row__actions')) menuOpen.value = null;
}

/**
 * The menu is positioned once, on open, and this list scrolls inside the panel — so without
 * this it would hang in place while its row slid away. Nothing else in the planner does this
 * because no other menu of this kind sits in a scrolling list.
 */
function closeMenuOnScroll() {
  menuOpen.value = null;
}

onMounted(() => {
  document.addEventListener('click', closeMenuOnOutsideClick);
  document.querySelector('.actions-main')?.addEventListener('scroll', closeMenuOnScroll, true);
});

onUnmounted(() => {
  document.removeEventListener('click', closeMenuOnOutsideClick);
  document.querySelector('.actions-main')?.removeEventListener('scroll', closeMenuOnScroll, true);
});
</script>

<style scoped>
.turn-panel {
  display: flex;
  flex-direction: column;
}

.turn-stats {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.turn-stats__item {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
  padding: 0.75rem 0.5rem;
  text-align: center;
}

.turn-stats__item + .turn-stats__item {
  border-left: 1px solid rgba(255, 255, 255, 0.08);
}

.turn-stats__label {
  font-size: 0.65rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
}

.turn-stats__value {
  font-size: 0.95rem;
  font-weight: 700;
}

.turn-empty {
  padding: 1rem 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  font-size: 0.85rem;
  margin: 0;
}

.turn-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.turn-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.5rem 0.6rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
}

.turn-row:hover {
  background: rgba(255, 255, 255, 0.04);
}

/* The row the walker is looking at, in the same yellow the banner uses for the next turn. */
.turn-row--selected {
  background: rgba(255, 214, 0, 0.08);
}

/* Marked down its leading edge as well as shaded. The shade alone is two steps off the panel
   and all but disappears on a bright screen, which is where this list is read. */
.turn-row--selected::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 1.35rem;
  border-radius: 0 2px 2px 0;
  background: #ffd600;
}

.turn-row--selected .turn-row__distance,
.turn-row--selected .turn-row__icon {
  color: #ffd600;
}

.turn-row__icon {
  flex: 0 0 auto;
  font-size: 1.35rem;
  color: rgba(255, 255, 255, 0.85);
}

.turn-row__text {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}

/* Distance leads, the way a printed route card does: the list is scanned for "how far until
   the next thing happens", and the instruction is read once that has been found. */
.turn-row__distance {
  font-weight: 700;
  font-size: 0.95rem;
}

/* The instruction itself is the content of the row, not a caption — it was set two steps
   down from the distance above it and read as disabled. */
.turn-row__instruction {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.82);
  overflow-wrap: anywhere;
}

/* The smallest text on the screen, and it was the faintest too: white at 0.4 over the
   panel's near-black is about 3.8:1, under the 4.5:1 floor for body text. 0.65 clears it
   while still sitting behind the instruction in the hierarchy. */
.turn-row__cumulative {
  flex: 0 0 auto;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.65);
  white-space: nowrap;
}

.turn-row__actions {
  flex: 0 0 auto;
}

.turn-row__actions .icon-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 0.25rem;
  line-height: 0;
}

.turn-row__actions .icon-btn:hover {
  color: #ffd600;
}

/* Fixed, so the panel's `overflow: hidden` cannot clip it. */
.menu-dropdown {
  position: fixed;
  z-index: 1000;
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  min-width: 8rem;
  background: #2f303b;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.45);
}

.menu-dropdown button {
  display: block;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 0.82rem;
  padding: 0.45rem 0.85rem;
  cursor: pointer;
}

.menu-dropdown button:hover {
  background: rgba(255, 255, 255, 0.08);
}

.menu-dropdown__danger {
  color: #ff6b6b;
}
</style>
